import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
  Stack,
  Alert,
} from "@mui/material";

// Icons
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SecurityIcon from "@mui/icons-material/Security";
import LockResetIcon from "@mui/icons-material/LockReset";
import EventIcon from "@mui/icons-material/Event";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import StatusChip from "../components/common/StatusChip";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AppEmailModal from "../components/common/AppEmailModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

// Redux Actions
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getUserById } from "../redux/actions/userActions";
import { forgotPassword } from "../redux/actions/authActions";
import { userActions } from "../redux/slices/user";
//import { UserResponseDto } from '@shared/dtos/user/UserResponseDto'

const ProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();

  // --- Redux State ---
  const {
    item: user,
    loadingFetchOne: loadingUser,
    errorFetchOne: errorLoadingUser,
    serverStatus,
  } = useAppSelector((state) => state.user);
  const { userInfo } = useAppSelector((state) => state.auth);

  const { loadingForgotPassword } = useAppSelector((state) => state.auth);
  const { company } = useAppSelector((state) => state.company);

  // --- Modal States ---
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState<boolean>(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState<boolean>(false);

  // Fetch full user details on mount
  useEffect(() => {
    if (userInfo && userInfo.id) {
      dispatch(getUserById(userInfo.id));
    }
  }, [dispatch, userInfo]);

  // Handle successful password reset request
  useEffect(() => {
    if (
      serverStatus?.operation === "forgot-password" &&
      serverStatus?.status === 200
    ) {
      setIsResetConfirmOpen(false);
      setIsEmailModalOpen(false);
      dispatch(userActions.setServerStatus(null));
    }
  }, [serverStatus, dispatch]);

  // --- Handlers ---
  const handleResetClick = (): void => {
    if (user?.email) {
      setIsResetConfirmOpen(true);
    } else {
      setIsEmailModalOpen(true);
    }
  };

  const handleConfirmExistingEmail = (): void => {
    if (company && user?.email) {
      // @ts-ignore - Assuming forgotPassword takes this format
      dispatch(
        forgotPassword({ companyCode: company.companyCode, email: user.email }),
      );
    }
  };

  const handleSubmitNewEmail = (enteredEmail: string): void => {
    if (company) {
      dispatch(
        forgotPassword({
          companyCode: company.companyCode,
          email: enteredEmail,
        }),
      );
    }
  };

  // --- Helper to Group Permissions ---
  const groupedPermissions = useMemo<Record<string, string[]>>(() => {
    if (!user || !user.permissions) return {};
    return user.permissions.reduce(
      (acc: Record<string, string[]>, permission: string) => {
        const [group] = permission.split(":");
        if (!acc[group]) acc[group] = [];
        acc[group].push(permission);
        return acc;
      },
      {},
    );
  }, [user]);

  const renderContent = (): React.JSX.Element => {
    if (loadingUser) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", my: 10 }}>
          <AppCircularProgress />
        </Box>
      );
    }

    if (errorLoadingUser) {
      return (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Alert severity="error" sx={{ display: "inline-flex" }}>
            {errorLoadingUser}
          </Alert>
          <Box sx={{ mt: 2 }}>
            <AppButton
              onClick={() => {
                if (userInfo) dispatch(getUserById(userInfo.id));
              }}
            >
              Retry
            </AppButton>
          </Box>
        </Box>
      );
    }

    if (!user) {
      return (
        <Typography sx={{ textAlign: "center", mt: 5 }}>
          Loading profile data...
        </Typography>
      );
    }

    const fullName = `${user.firstName} ${user.lastName}`;

    return (
      <Grid container spacing={3}>
        {/* LEFT COLUMN: User Identity & Account Details */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            {/* Identity Card */}
            <Card
              sx={{
                borderRadius: 2,
                border: `1px solid`,
                borderColor: "divider",
              }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: 100,
                    height: 100,
                    mx: "auto",
                    mb: 2,
                    boxShadow: 3,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 50, color: "#fff" }} />
                </Avatar>

                <Typography variant="h5" sx={{ fontWeight: "700", mb: 1 }}>
                  {fullName}
                </Typography>

                <Chip
                  label={user.userRole.replace(/_/g, " ")}
                  color="primary"
                  icon={<AdminPanelSettingsIcon />}
                  sx={{ mb: 2, fontWeight: "bold" }}
                />

                <Divider sx={{ my: 2 }} />

                <Stack spacing={2} sx={{ textAlign: "left" }}>
                  <Stack
                    direction="row"
                    sx={{ alignItems: "center", gap: 1.5 }}
                  >
                    <EmailIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {user.email || "No email registered"}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    sx={{ alignItems: "center", gap: 1.5 }}
                  >
                    <CheckCircleIcon color="action" fontSize="small" />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2">Status:</Typography>
                      <StatusChip status={user.status} />
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    sx={{ alignItems: "center", gap: 1.5 }}
                  >
                    {user.locked ? (
                      <LockIcon color="error" fontSize="small" />
                    ) : (
                      <LockOpenIcon color="success" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: user.locked ? "error.main" : "success.main",
                      }}
                    >
                      {user.locked ? "Account Locked" : "Account Unlocked"}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    sx={{ alignItems: "center", gap: 1.5 }}
                  >
                    <EventIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      Joined: {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card
              sx={{
                borderRadius: 2,
                border: `1px solid`,
                borderColor: "divider",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  sx={{ alignItems: "center", gap: 1, mb: 2 }}
                >
                  <SecurityIcon color="secondary" />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Account Security
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Password Management
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mb: 2 }}>
                  Think your password has been compromised? Request a secure
                  reset link to update it.
                </Typography>

                <AppButton
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  startIcon={<LockResetIcon />}
                  onClick={handleResetClick}
                >
                  Reset Password
                </AppButton>
              </CardContent>
            </Card>

            {/* Read Only Notice */}
            <Box
              sx={{
                p: 2,
                bgcolor: "background.default",
                borderRadius: 1,
                border: `1px solid`,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center" }}
              >
                Your profile information and permissions are managed by the
                System Administrator. Contact support if changes are required.
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* RIGHT COLUMN: Permissions Grid */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              border: `1px solid`,
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 2, mb: 3 }}
              >
                <Avatar
                  sx={{
                    bgcolor: "success.main",
                    width: 50,
                    height: 50,
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 26, color: "#fff" }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: "700" }}>
                    Effective Permissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access rights granted to your role (
                    {user.permissions?.length || 0} total)
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />

              {Object.keys(groupedPermissions).length > 0 ? (
                <Grid container spacing={3}>
                  {Object.entries(groupedPermissions)
                    .sort()
                    .map(([groupName, perms]) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={groupName}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            border: `1px solid`,
                            borderColor: "divider",
                            bgcolor: "background.default",
                            height: "100%",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              textTransform: "capitalize",
                              mb: 1.5,
                            }}
                          >
                            {groupName.replace(/_/g, " ")}
                          </Typography>
                          <Stack spacing={1}>
                            {perms.sort().map((p) => {
                              const action = p.split(":")[1];
                              return (
                                <Chip
                                  key={p}
                                  label={action}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    justifyContent: "flex-start",
                                    borderRadius: 1,
                                    borderColor: "divider",
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      </Grid>
                    ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography color="text.secondary">
                    No specific permissions assigned to this account.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* --- MODALS --- */}
        <ConfirmDialog
          open={isResetConfirmOpen}
          onClose={() => setIsResetConfirmOpen(false)}
          onConfirm={handleConfirmExistingEmail}
          loading={loadingForgotPassword}
          title="Request Password Reset?"
          description={`We will send a secure password reset link to ${user?.email}. You will need to use that link to set a new password.`}
          confirmText="Send Link"
        />

        <AppEmailModal
          open={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSubmit={handleSubmitNewEmail}
          loading={loadingForgotPassword}
          title="Send Password Reset Link"
          description="Your account doesn't have an email address on file. Please provide an email where we can send the secure reset link."
          submitText="Send Link"
        />
      </Grid>
    );
  };

  return (
    <PageContainer
      title="My Profile"
      subtitle="View your personal details, role, and system access rights."
      breadcrumbs={[
        { label: "Dashboard", to: "/dashboard" },
        { label: "Profile", to: undefined },
      ]}
    >
      {renderContent()}
    </PageContainer>
  );
};

export default ProfilePage;
